import { useEffect, useMemo, useState } from "react";
import "../../../../../css/AML/CheckDetails/DataSources/DataSources.css";
import "../../../../../css/AML/CheckDetails/CheckDetails.css";

import StatusButton from "../../Components/Buttons/StatusButton";
import ToggleSwitch from "../../Components/Buttons/ToggleSwitch";
import AMLInfoTooltip from "../../Components/Tooltip/AMLInfoTooltip";
import AMLWarningTooltip from "../../Components/Tooltip/AMLWarningTooltip";

import { ReactComponent as ArrowUp } from "../../../../../assets/icons/aml/arrowUp.svg";
import { ReactComponent as InfoIcon } from "../../../../../assets/icons/aml/information.svg";
import { ReactComponent as WarningIcon } from "../../../../../assets/icons/aml/warning.svg";

import capitalizeFirst from "../../Checks/utils/factories/capitalise";
import prettifyEnumName from "../Utils/prettifyEnumName";
import experianServiceNames from "../Utils/Experian/getExperianServiceNames";

import { getDecisionForService } from "../Utils/Experian/getServiceDecision";
import { colorForResult } from "../Utils/getColorForDecision";
import { dataSourceAlertMap } from "../Utils/Experian/flagDataSourceAlert";
import { alertsConfig } from "../Utils/Experian/getItemsAlerts";

import { getMortalityDecision } from "../Utils/Experian/getMortalityDetails";
import { persistMortalityNotClientOverrideToggle } from "../Utils/Experian/updateExperianResponse";
import Banner from "../../Components/Banners/Banner";
import {  useCheck2GuideUrl } from "../../Checks/utils/guide/getUrl";

const normTxt = (v) => (v ?? "").toString().trim().toLowerCase();

const SHOW_ALL_DATA_SOURCES_FOR_TESTING = false;

const endsWithCaps = (row) => {
  const clean = (s) => normTxt(s).replace(/[)\].,:;_-]+$/g, "");
  const k = clean(row?.key);
  const l = clean(row?.label);
  return k.endsWith("caps") || l.endsWith("caps");
};

const endsWithCais = (row) => {
  const clean = (s) => normTxt(s).replace(/[)\].,:;_-]+$/g, "");
  const k = clean(row?.key);
  const l = clean(row?.label);
  return k.endsWith("cais") || l.endsWith("cais");
};

const getCapsTooltipText = () => "This data source relates to CAPS.";
const getCaisTooltipText = () => "This data source relates to CAIS.";

const getCapsCaisTooltipText = (row) => {
  const clean = (s) => normTxt(s).replace(/[)\].,:;_-]+$/g, "");
  const k = clean(row?.key);
  const l = clean(row?.label);

  if (k.endsWith("caps") || l.endsWith("caps"))
    return "This data source relates to CAPS.";
  if (k.endsWith("cais") || l.endsWith("cais"))
    return "This data source relates to CAIS.";
  return "This data source relates to CAIS / CAPS.";
};

// Read override regardless of whether caller passes wrapper doc or inner response
const getMortalityOverride = (maybeWrapperOrInner) => {
  const r = maybeWrapperOrInner?.response ?? maybeWrapperOrInner ?? null;
  return r?.clientResponsePayload?.overrides?.mortality ?? null;
};

const DatabaseVerification = ({
  response,
  checkId,
  ownerEmail,
  serviceName = "uk-idv",
  userEmail,
}) => {
  const [open, setOpen] = useState(false);

  // toggle save state
  const [savingMortality, setSavingMortality] = useState(false);
  const [mortalityErr, setMortalityErr] = useState("");
const check2GuideUrl = useCheck2GuideUrl();
  // unwrap Firestore shape once
  const effectiveResponse = useMemo(
    () => response?.response ?? response ?? {},
    [response],
  );

  // ---------- Data Sources table ----------
  const { visibleRows, totalChecks, totalSources, allDataCounts } =
    useMemo(() => {
      const payload = effectiveResponse?.clientResponsePayload;
      if (!payload) {
        return {
          visibleRows: [],
          totalChecks: 0,
          totalSources: 0,
          allDataCounts: [],
        };
      }

      const aliases = Array.isArray(experianServiceNames.ID_VERIFICATION)
        ? serviceName
        : [serviceName];

      // console.log(experianServiceNames.ID_VERIFICATION);

      // console.log("Aliases for service name", serviceName, ":", aliases);
      const templateElement = payload.decisionElements?.find((d) =>
        aliases.some((s) => normTxt(d?.serviceName) === normTxt(s)),
      );

      if (!templateElement) {
        return {
          visibleRows: [],
          totalChecks: 0,
          totalSources: 0,
          allDataCounts: [],
        };
      }

      const template =
        templateElement?.otherData?.response?.dataLevelTemplate || {};
      const enums = [
        ...(template.primary || []),
        ...(template.secondary || []),
      ];
      const counts = templateElement.dataCounts || [];

      const rows = enums.map((enumName) => {
        const enumLower = enumName.toLowerCase();

        const checks = counts
          .filter(
            (dc) =>
              (dc.name || "").toLowerCase().startsWith("itemscurrentaddress") &&
              (dc.name || "").toLowerCase().includes(enumLower),
          )
          .reduce((sum, dc) => sum + (dc.value || 0), 0);

        const sources = counts
          .filter((dc) => {
            const name = (dc?.name || "").toLowerCase();
            const isDsType = name.startsWith("datasourcetypescurrentaddress");
            const isDs = name.startsWith("datasourcescurrentaddress");
            return (isDsType || isDs) && name.includes(enumLower);
          })
          .reduce((sum, dc) => sum + (dc?.value || 0), 0);

        return {
          key: enumName,
          label: prettifyEnumName(enumName),
          checks,
          sources,
        };
      });

      const filteredRows = SHOW_ALL_DATA_SOURCES_FOR_TESTING
        ? rows
        : rows.filter((r) => r.checks > 0 || r.sources > 0);

      // group ONLY CAPS
      const capsRows = filteredRows.filter(endsWithCaps);
      const nonCapsRows = filteredRows.filter((r) => !endsWithCaps(r));

      const capsGroupedRow =
        capsRows.length > 0
          ? {
              key: "caps_group",
              label: "Secondary Sources",
              checks: capsRows.reduce((s, r) => s + r.checks, 0),
              sources: capsRows.reduce((s, r) => s + r.sources, 0),
              _isCapsGroup: true,
            }
          : null;

      const visible = capsGroupedRow
        ? [...nonCapsRows, capsGroupedRow]
        : nonCapsRows;

      return {
        visibleRows: visible,
        totalChecks: visible.reduce((s, r) => s + r.checks, 0),
        totalSources: visible.reduce((s, r) => s + r.sources, 0),
        allDataCounts: counts,
      };
    }, [effectiveResponse, serviceName]);

  // if (!visibleRows.length) return null;

  // ---------- Overall decision ----------
  const rawDecision = useMemo(
    () =>
      getDecisionForService(
        effectiveResponse,
        experianServiceNames.ID_VERIFICATION,
      ),
    [effectiveResponse],
  );

  const decisionLower = rawDecision ? String(rawDecision).toLowerCase() : null;
  const decision = decisionLower ? capitalizeFirst(decisionLower) : null;

  // ---------- Mortality toggle (source of truth: Firestore override) ----------
  const overrideFromResponse = useMemo(
    () => getMortalityOverride(response), // use wrapper-safe reader
    [response],
  );
  const overrideExists = !!overrideFromResponse;

  // optimistic state: keep until response catches up
  const [overrideOptimistic, setOverrideOptimistic] = useState(null);
  const toggleChecked =
    overrideOptimistic !== null ? overrideOptimistic : overrideExists;

  // clear optimistic when firestore-driven value matches it
  useEffect(() => {
    if (overrideOptimistic === null) return;
    if (overrideExists === overrideOptimistic) setOverrideOptimistic(null);
  }, [overrideExists, overrideOptimistic]);

  // Mortality decision should respect the toggle (optimistic included)
  const mortalityDecision = useMemo(() => {
    const mort = getMortalityDecision(
      effectiveResponse,
      experianServiceNames.CREDIT_REFERENCE,
    );

    if (mort?.status === "needs_user_confirmation" && toggleChecked) {
      return {
        ...mort,
        status: "pass_overridden",
        label: "Pass",
        reason: "Mortality match overridden by user confirmation",
      };
    }

    return mort;
  }, [effectiveResponse, toggleChecked]);

  const mortalityLabel =
    mortalityDecision?.status === "pass" ||
    mortalityDecision?.status === "pass_overridden"
      ? "Pass"
      : mortalityDecision?.status === "needs_user_confirmation"
        ? "Fail"
        : "Unknown";

  const mortalityClass =
    mortalityDecision?.status === "pass" ||
    mortalityDecision?.status === "pass_overridden"
      ? "yes"
      : "no";

  // rental/mortgage special alert handling (unchanged)
  const hasRentalLeasing = useMemo(
    () => visibleRows.some((r) => normTxt(r.label) === "rental leasing"),
    [visibleRows],
  );

  const hasMortgage = useMemo(
    () => visibleRows.some((r) => normTxt(r.label) === "mortgage"),
    [visibleRows],
  );

  const bothMR = hasMortgage && hasRentalLeasing;
  const showRentalMortgageAlerts = bothMR || hasRentalLeasing || hasMortgage;

  const hasLoanShort = useMemo(() => {
    const payload = effectiveResponse?.clientResponsePayload;
    if (!payload) return false;

    const allDataCountsLocal =
      allDataCounts ||
      (payload.decisionElements || []).flatMap((d) => d.dataCounts || []);

    const loanCfg = alertsConfig.find(
      (c) => normTxt(c.key) === "loan short" || normTxt(c.key) === "loanshort",
    );

    if (loanCfg?.type === "dataCount" && loanCfg.fieldName) {
      const v = allDataCountsLocal
        .filter((dc) => normTxt(dc?.name) === normTxt(loanCfg.fieldName))
        .reduce((s, dc) => s + (dc?.value || 0), 0);
      return v > 0;
    }

    // fallback if needed
    const v = allDataCountsLocal
      .filter((dc) => normTxt(dc?.name).includes("loanshort"))
      .reduce((s, dc) => s + (dc?.value || 0), 0);

    return v > 0;
  }, [effectiveResponse, allDataCounts]);

  // ---------- Alerts rows (only Mortality used here, keep your config pattern) ----------
  const alertRows = useMemo(() => {
    const payload = effectiveResponse?.clientResponsePayload;
    if (!payload) return [];

    const allDataCountsLocal =
      allDataCounts ||
      (payload.decisionElements || []).flatMap((d) => d.dataCounts || []);

    return alertsConfig.map((cfg) => {
      if (normTxt(cfg.key) === "mortality") {
        return { ...cfg, mortalityDecision };
      }

      // Loan Short rule: flag ONLY if BOTH Rental Leasing + Mortgage are present
      if (
        normTxt(cfg.key) === "loan short" ||
        normTxt(cfg.key) === "loanshort"
      ) {
        const shouldFlag = hasLoanShort; //  ONLY when Loan Short present
        return {
          ...cfg,
          value: shouldFlag ? 1 : 0,
          status: shouldFlag ? "fail" : "pass",
        };
      }

      // default behaviour
      let value = 0;

      if (cfg.type === "dataCount" && cfg.fieldName) {
        value = allDataCountsLocal
          .filter((dc) => normTxt(dc.name) === normTxt(cfg.fieldName))
          .reduce((sum, dc) => sum + (dc.value || 0), 0);
      }

      if (cfg.type === "orchestratorDecision") {
        const d = (
          getDecisionForService(
            effectiveResponse,
            experianServiceNames.ID_VERIFICATION,
          ) || ""
        ).toLowerCase();
        value = d === "continue" ? 1 : 0;
      }

      return {
        ...cfg,
        value,
        status: value === 0 ? "fail" : "pass",
      };
    });
  }, [
    effectiveResponse,
    allDataCounts,
    mortalityDecision,
    showRentalMortgageAlerts,
  ]);

  const mortalityAlert = alertRows.find((r) => normTxt(r.key) === "mortality");

  // ---------- Toggle handler ----------
  const onToggleMortality = async (next) => {
    setMortalityErr("");
    setSavingMortality(true);
    setOverrideOptimistic(next);

    try {
      if (!ownerEmail) throw new Error("ownerEmail is missing");
      if (!checkId) throw new Error("checkId is missing");

      await persistMortalityNotClientOverrideToggle({
        ownerEmail,
        checkId,
        enableOverride: next,
        confirmedBy: userEmail || null,
      });

      // keep optimistic until response refresh updates overrideExists
      // (effect above will clear it automatically)
    } catch (e) {
      setOverrideOptimistic(null);
      setMortalityErr(e?.message || "Failed to update override");
    } finally {
      setSavingMortality(false);
    }
  };
  const showMortalityToggle =
    // show if override exists (so user can turn it off)
    toggleChecked === true ||
    // or show if it's a fail / needs confirmation (so user can override)
    mortalityDecision?.status === "needs_user_confirmation";

  if (!decision) return null;
  return (
    <div className="Result-Section">
      <div
        className="Result-Section-Header"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)
        }
      >
        <div className="AML-Text-Subtitle">Database Verification</div>
        <div className="Result-Section-Row">
          {decision && (
            <StatusButton
              type={decision || "Not Started"}
              color={colorForResult(decisionLower)}
            />
          )}

          <ArrowUp
            className={`collapse-icon ${open ? "collapsed" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={open ? "Expand section" : "Collapse section"}
            aria-expanded={!open}
            // onClick={() => setOpen((prev) => !prev)}
            // onKeyDown={(e) =>
            //   (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)
            // }
          />
        </div>
      </div>

      <div className={`rs-body ${!open ? "is-collapsed" : ""}`}>
        <div className="rs-body-inner" style={{ gap: 0 }}>
          {open && (
            <>
              {/* Data Sources table header */}
              <div className="SubHeader Result-Section-Header ds-header-4">
                <div className="AML-Text-Input">Data Sources</div>

                <div>
                  <AMLInfoTooltip
                    text="Checks: The number of credit agreements or public records linked to the individual at their current address held by the Credit Reference Agency."
                    position="left"
                  >
                    <div className="AML-Text-Input">Checks ({totalChecks})</div>
                    <InfoIcon className="InfoIcon" />
                  </AMLInfoTooltip>
                </div>

                <div>
                  <AMLInfoTooltip
                    text="Sources: Indicates how many different data sources were used to find these checks."
                    position="left"
                  >
                    <div className="AML-Text-Input">
                      Sources ({totalSources})
                    </div>
                    <InfoIcon className="InfoIcon" />
                  </AMLInfoTooltip>
                </div>

                <div className="AML-Text-Input">Info</div>
              </div>

              {/* Data Sources rows */}
              <div className="ds-table">
                <div className="ds-grid">
                  {visibleRows.map((row, idx) => {
                    const labelLower = normTxt(row.label);
                    const isRental = labelLower === "rental leasing";
                    const isMortgageRow = labelLower === "mortgage";
                    const isLoanShort = labelLower === "loan short";

                    const shouldFlagMortgage = bothMR;
                    const shouldFlagRental = bothMR;
                    // const shouldFlagMortgage =
                    //   bothMR || (hasLoanShort && hasMortgage);
                    // const shouldFlagRental =
                    //   bothMR || (hasLoanShort && hasRentalLeasing);

                    // existing base meta
                    const baseMeta = !(isRental || isMortgageRow || isLoanShort)
                      ? dataSourceAlertMap[row.label]
                      : undefined;

                    let hasAlert = !!baseMeta;
                  let tooltipText = null;
                  let tooltipContent = null;

                  if (baseMeta?.tooltip) {
                    if (typeof baseMeta.tooltip === "function") {
                      tooltipContent = baseMeta.tooltip(check2GuideUrl);
                    } else {
                      tooltipText = baseMeta.tooltip;
                    }
                  }
                    if (isMortgageRow && shouldFlagMortgage) {
                      hasAlert = true;

                      tooltipContent = (
                        <>
                          Early Indicator - this client has both a mortgage and
                          Rental Leasing as a positive source. See{" "}
                          <a
                            className="AML-Text-Input"
                            style={{
                              gap: "10px",
                              cursor: check2GuideUrl
                                ? "pointer"
                                : "not-allowed",
                              opacity: check2GuideUrl ? 1 : 0.5,
                            }}
                            onClick={() => {
                              if (!check2GuideUrl) return;
                              window.open(
                                check2GuideUrl,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                          >
                            check² guide
                          </a>{" "}
                          for further information.
                        </>
                      );
                    }

                    if (isRental && shouldFlagRental) {
                      hasAlert = true;

                      tooltipContent = (
                        <>
                          Early Indicator - this client has both a mortgage and
                          Rental Leasing as a positive source. See{" "}
                          <a
                            className="AML-Text-Input"
                            style={{
                              gap: "10px",
                              cursor: check2GuideUrl
                                ? "pointer"
                                : "not-allowed",
                              opacity: check2GuideUrl ? 1 : 0.5,
                            }}
                            onClick={() => {
                              if (!check2GuideUrl) return;
                              window.open(
                                check2GuideUrl,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                          >
                            check² guide
                          </a>{" "}
                          for further information.
                        </>
                      );
                    }

                    return (
                      <div
                        className={`ds-row-4 ${
                          idx % 2 !== 0 ? "ds-row-even" : "ds-row-odd"
                        } ${hasAlert ? "ds-row-alert" : ""}`}
                        key={row.key}
                      >
                        <div
                          className="AML-Text-Body"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span>{row.label}</span>

                          {/* CAPS group tooltip */}
                          {row._isCapsGroup && (
                            <AMLInfoTooltip
                              text="Other reliable sources to verify client's identity. "
                              position="right"
                            >
                              <InfoIcon
                                className="InfoIcon"
                                style={{ cursor: "pointer" }}
                              />
                            </AMLInfoTooltip>
                          )}

                          {/* CAIS tooltip (optional: keep if you still want it) */}
                          {/* {!row._isCapsGroup && endsWithCais(row) && (
                            <AMLInfoTooltip
                              text={getCaisTooltipText()}
                              position="right"
                            >
                              <InfoIcon
                                className="InfoIcon"
                                style={{ cursor: "pointer" }}
                              />
                            </AMLInfoTooltip>
                          )} */}
                        </div>

                        <div className="AML-Text-Body">{row.checks}</div>
                        <div className="AML-Text-Body">{row.sources}</div>

                        <div className="AML-Text-Body">
                          {hasAlert && (
                            <AMLWarningTooltip
                              text={
                                tooltipText ||
                                tooltipContent ||
                                "This data source has triggered an alert."
                              }
                              position="left"
                            >
                              
                              <WarningIcon className="WarningIcon" />
                            </AMLWarningTooltip>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="veriticalSizedBox" />

              {/* Alerts (Mortality) */}
              {mortalityAlert && (
                <>
                  <div className="SubHeader Result-Section-Header ds-header-4">
                    <div className="AML-Text-Input">Alerts</div>
                    {/* <div className="AML-Text-Input">
                      {showMortalityToggle ? "Change Status" : ""}
                      </div> */}
                    <div className="AML-Text-Input"></div>
                    <div className="AML-Text-Input">Status</div>
                    <div className="AML-Text-Input" style={{ display: "none" }}>
                      Info
                    </div>
                  </div>

                  <div className="ds-table">
                    <div className="ds-grid">
                      <div className={`ds-row-4`} key={mortalityAlert.key}>
                        <div className="AML-Text-Body">
                          {mortalityAlert.label}
                        </div>
                        <div style={{ width: 44 }} />

                        <div className="AML-Text-Body">
                          <span className={`MatchStatus-${mortalityClass}`}>
                            {mortalityLabel}
                          </span>
                        </div>

                        {/* {showMortalityToggle ? (
                          <ToggleSwitch
                            checked={toggleChecked}
                            disabled={savingMortality}
                            label=""
                            onChange={onToggleMortality}
                          />
                        ) : (
                          // optional: keep column alignment (or just render null)
                          <div style={{ width: 44 }} />
                        )} */}
                        <div
                          className="AML-Text-Body"
                          style={{ display: "none" }}
                        >
                          <AMLInfoTooltip
                            text={
                              mortalityErr ||
                              mortalityDecision?.reason ||
                              mortalityAlert.tooltip
                            }
                            position="left"
                          >
                            <InfoIcon className="InfoIcon" />
                          </AMLInfoTooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                  {mortalityLabel === "Fail" && (
                    <div style={{ padding: 16 }}>
                      <Banner
                        type="warning"
                        message="Refer check to a member of the compliance team."
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseVerification;
